import { Configuration, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { sign } from 'jsonwebtoken';
import { getAppConfig } from './config';
import jwtDecode from 'jwt-decode';
import { UserDetails } from 'types/domainTypes';

interface JwtToken {
  name: string;
  unique_name: string;
}

let pca: IPublicClientApplication;

export const getAuthConfig = (): Configuration => ({
  auth: {
    clientId: getAppConfig().aadClientId,
    authority: 'https://login.microsoftonline.com/9fc58307-b8c4-416e-bc27-eab7ad86b306',
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
});

export function getInstance(): IPublicClientApplication {
  if (pca) {
    return pca;
  }

  pca = new PublicClientApplication(getAuthConfig());

  if (process.env.REACT_APP_SERVER_MOCKED || process.env.REACT_APP_AUTH_MOCKED) {
    const mockUser = {
      accountIdentifier: 'e11d4d0c-1c70-430d-a644-aed03a60e059',
      homeAccountIdentifier: '',
      username: 'mock@example.net',
      name: 'Mock User',
      idToken: null,
      sid: '',
      environment: '',
      idTokenClaims: {
        tid: 'fake-tenant',
      },
    };

    pca.loginRedirect = async () => {
      localStorage.setItem('mockAccount', JSON.stringify(mockUser));
    };

    pca.getAllAccounts = () => {
      const mockAccount = localStorage.getItem('mockAccount');
      return mockAccount ? [JSON.parse(mockAccount)] : [];
    };

    pca.acquireTokenSilent = async () => ({
      authority: '',
      uniqueId: '',
      tenantId: '',
      scopes: [],
      account: null,
      idToken: sign(mockUser, 'mockSecret'),
      idTokenClaims: {},
      accessToken: '',
      fromCache: false,
      expiresOn: null,
      tokenType: '',
      correlationId: '',
    });
  }

  return pca;
}

export async function getAuthorizationToken(getAccessToken?: boolean): Promise<string> {
  const request = { scopes: [] };
  const instance = getInstance();
  let idToken = '';
  let accessToken = '';

  try {
    const result = await instance.acquireTokenSilent(request);
    idToken = result.idToken;
    accessToken = result.accessToken;
  } catch {
    // Fallback to redirecting the user to login if this fails.
    await instance.acquireTokenRedirect(request);
  }

  return getAccessToken ? accessToken : idToken;
}

export async function prepareAuthHeaders(headers: Headers): Promise<Headers> {
  const token = await getAuthorizationToken();
  headers.set('authorization', `Bearer ${token}`);
  return headers;
}

export async function getUserDetails(): Promise<UserDetails> {
  try {
    const jwtTokenString: string = await getAuthorizationToken(true);
    const jwtToken: JwtToken = jwtDecode(jwtTokenString) as JwtToken;
    return {
      name: jwtToken.name,
      email: jwtToken.unique_name,
    };
  } catch (error) {
    console.error(error);
    return {
      name: '',
      email: '',
    };
  }
}

export async function getUserPhoto(): Promise<string | undefined> {
  try {
    const accessToken = await getAuthorizationToken(true);
    const headers = new Headers();
    headers.append('Authorization', `Bearer ${accessToken}`);

    const res = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
      method: 'GET',
      headers: headers,
    });

    if (res.ok) {
      const blob = await res.blob();
      const urlObject = URL.createObjectURL(blob);
      return urlObject;
    }
  } catch (error) {
    console.error(error);
    return undefined;
  }
}
